import { z } from 'zod';
import { idSchema } from './common.js';
/**
 * Leve (the client / COR platform, repo Remote-Leverage/hriq) on the CRM project page: what Leve's
 * organisation page shows on its "Payments" and "Fees & Billing" tabs, read-only, straight from
 * Leve's Supabase tables, as the read-only `crm_reader` role (`scripts/leve/crm-reader.sql`).
 *
 * A project finds its Leve organisations through RecruitCRM: Leve keeps the RCRM CONTACT slug on
 * the organisation (`recruit_crm_contact_slug`), the CRM keeps a contact's RCRM slug in
 * `rcrm_sync_links`. The project's contacts (its own and those working at its company) give the
 * slugs; every Leve organisation with one of them is shown.
 *
 * Money is in cents (integers) wherever Leve computes in cents; Leve's text/decimal amounts are
 * kept as the decimal strings Leve stores, so nothing is rounded on the way.
 */
/** Why the tab has nothing to show, so its empty state can say so. */
export const LEVE_STATUSES = [
    /** Read Leve; `organizations` may still be empty (no contact slug, or no Leve org has one). */
    'ready',
    /** No Leve database configured for this environment (or its secret is empty / invalid). */
    'not_configured',
    /** Configured, but Leve did not answer (down, breaker open, credentials refused). */
    'unavailable',
];
export const levePaymentSchema = z.object({
    id: z.string(),
    contractorName: z.string(),
    paymentType: z.string(),
    /** As Leve stores it (text): normally a decimal, shown as is when it is not. */
    amount: z.string(),
    currency: z.string(),
    paymentMethod: z.string().nullable(),
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    status: z.string(),
    createdAt: z.string(),
});
export const leveAgreementSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** `percentage` | `per_contractor` | `flat`. */
    feeType: z.string(),
    feeAmount: z.string(),
    /** `per_pay_run` | `4_weekly` | `monthly` | `yearly`. */
    billingCycle: z.string(),
    contractorSeats: z.number().int().nullable(),
    /** `active` | `trial` | `paused` | `cancelled`. */
    status: z.string(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    cancelReason: z.string().nullable(),
    freeTrialDays: z.number().int(),
    notes: z.string().nullable(),
});
export const leveSeatSchema = z.object({
    id: z.string(),
    /** "Seat 3": numbered over every seat ever sold, oldest first. */
    number: z.number().int(),
    occupantName: z.string().nullable(),
    /** False when the occupant is no longer working there (Leve: "(no longer active)"). */
    occupantCurrent: z.boolean().nullable(),
    depositWaived: z.boolean(),
    depositHeldCents: z.number().int(),
    depositInvoicedUnpaidCents: z.number().int(),
    startDate: z.string(),
});
/** Leve's `SeatDepositState`. */
export const LEVE_SEAT_DEPOSIT_STATES = [
    'vacant',
    'waived',
    'covered',
    'unpriced',
    'never_collected',
    'needs_reup',
];
export const leveSeatDepositSchema = z.object({
    seatId: z.string(),
    number: z.number().int(),
    occupantName: z.string().nullable(),
    requiredCents: z.number().int(),
    heldCents: z.number().int(),
    shortfallCents: z.number().int(),
    state: z.enum(LEVE_SEAT_DEPOSIT_STATES),
    openDepositInvoice: z.object({ invoiceNumber: z.string(), status: z.string() }).nullable(),
});
export const leveDepositEntrySchema = z.object({
    id: z.string(),
    kind: z.string(),
    amount: z.string(),
    note: z.string(),
    seatOccupant: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
});
export const leveOrganizationSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Leve's organisation page, for "Open in Leve"; null when the CRM has no Leve URL configured. */
    url: z.string().nullable(),
    fundingModel: z.string().nullable(),
    /** The CRM contacts whose RecruitCRM slug linked this organisation. */
    matchedContacts: z.array(z.object({ id: idSchema, name: z.string() })),
    payments: z.object({
        /** Over EVERY payment, as Leve's summary strip (not just the listed ones). */
        completedTotal: z.string(),
        pendingTotal: z.string(),
        count: z.number().int(),
        /** The newest 50, as Leve lists them. */
        items: z.array(levePaymentSchema),
    }),
    billing: z.object({
        agreements: z.array(leveAgreementSchema),
        /** The newest active agreement (Leve's "Active Fee Agreement" card), when there is one. */
        activeAgreementId: z.string().nullable(),
        seats: z.object({
            /** Active seats only, as Leve's Seats panel lists them. */
            items: z.array(leveSeatSchema),
            activeContractors: z.number().int(),
            /** Seats sold minus active contractors; negative = more contractors than seats. */
            free: z.number().int(),
        }),
        deposits: z.object({
            agreedPerSeatCents: z.number().int(),
            requiredCents: z.number().int(),
            heldCents: z.number().int(),
            shortfallCents: z.number().int(),
            /** Held against no active seat (cancelled seats, unattached entries), unassigned included. */
            heldOffSeatCents: z.number().int(),
            /** Extra the client deposited that is on no seat yet. */
            unassignedCents: z.number().int(),
            seats: z.array(leveSeatDepositSchema),
            /** The newest 200 ledger entries. */
            entries: z.array(leveDepositEntrySchema),
        }),
    }),
});
export const leveProjectSchema = z.object({
    status: z.enum(LEVE_STATUSES),
    /** Why it is not `ready`, in words for the tab (never a secret or a connection string). */
    reason: z.string().nullable(),
    /** The project's contacts (its own and its company's) that carry a RecruitCRM slug, and not. */
    contactsWithSlug: z.number().int(),
    contactsWithoutSlug: z.number().int(),
    organizations: z.array(leveOrganizationSchema),
});
/**
 * The KPI strip of Leve's organisation page (`org-detail.tsx` `SummaryStrip`), one per Leve
 * organisation, for the project page's header. Money is in dollars as Leve's page computes it
 * (decimal strings, not cents): Leve shows `$<n.toLocaleString()>`.
 */
export const leveOrganizationKpisSchema = z.object({
    id: z.string(),
    name: z.string(),
    url: z.string().nullable(),
    activeContractors: z.number().int(),
    /** Active contractors when the organisation's funding model is `COR`, else 0. */
    activeCorVas: z.number().int(),
    seatsFilled: z.number().int(),
    seatsSold: z.number().int(),
    depositHeld: z.string(),
    clientOwes: z.string(),
    paidToContractors: z.string(),
    pendingToContractors: z.string(),
});
export const leveKpisSchema = z.object({
    status: z.enum(LEVE_STATUSES),
    reason: z.string().nullable(),
    organizations: z.array(leveOrganizationKpisSchema),
});
//# sourceMappingURL=leve.js.map